import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { finalize, map, of } from 'rxjs';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import {
  Filters,
  FnCaricamentoDati,
  SortBy,
} from 'src/app/components/lista-tabellare/classes/lista-tabellare-data-source';
import {
  BottoniListaEvent,
  Colonna,
  ListaTabellareComponent,
} from 'src/app/components/lista-tabellare/lista-tabellare.component';
import {
  Attivazione,
  LicenzeService,
  ParametriAttivazioni,
  PromotoreLicenze,
} from 'src/app/services/licenze/licenze.service';
import { RuoliList, UtenteService } from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DialogPacchettiAggiuntiviComponent } from '../dialog-pacchetti-aggiuntivi/dialog-pacchetti-aggiuntivi.component';

@Component({
  selector: 'app-dialog-attivazioni',
  templateUrl: './dialog-attivazioni.component.html',
  styleUrls: ['./dialog-attivazioni.component.scss'],
  standalone: false,
})
export class DialogAttivazioniComponent implements OnInit {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;
  @ViewChild('tabella') tabella!: ListaTabellareComponent;

  public formParametri!: FormGroup;

  public anni: number[] = [];
  public promotori: PromotoreLicenze[] = [];
  public ruoli: RuoliList[] = [];

  /**
   * Filtri dell'ultima esecuzione. Finché è undefined la tabella non chiama il ws.
   */
  private filtriEseguiti: Filters[] | undefined = undefined;

  /**
   * Ordinamento dell'ultimo caricamento della tabella: l'export deve rispettarlo
   */
  private sortByCorrente: SortBy[] = this.sortByDefault();

  public get isEseguito(): boolean {
    return !!this.filtriEseguiti;
  }

  public colonne: { [key: string]: Colonna } = {
    utente: {
      title: 'Utente',
      sortable: true,
      campiSort: ['cognome', 'nome'],
      value: (record: Attivazione) => this.descrizioneUtente(record),
    },
    emailAttivazione: {
      title: 'Email attivazione',
      sortable: true,
      value: (record: Attivazione) => record?.emailAttivazione || '-',
    },
    ruolo: {
      title: 'Ruolo',
      sortable: true,
      value: (record: Attivazione) => record?.ruolo || '-',
    },
    codicePromotore: {
      title: 'Promotore',
      sortable: true,
      value: (record: Attivazione) => record?.codicePromotore || '-',
    },
    ragioneSocialeAziende: {
      title: 'Aziende',
      sortable: false,
      value: (record: Attivazione) => this.uniscoValori(record?.ragioneSocialeAziende),
    },
    maxNrAziende: {
      title: 'Max nr. aziende',
      sortable: true,
      align: 'right',
      value: (record: Attivazione) => String(record?.maxNrAziende ?? 0),
    },
    tipoRinnovo: {
      title: 'Rinnovo Automatico',
      sortable: true,
      campiSort: ['rinnovoAutomatico'],
      align: 'center',
      value: (record: Attivazione) => this.rinnovoAutomatico(record),
    },
    dataPrimaAttivazione: {
      title: 'Cliente dal',
      sortable: true,
      value: (record: Attivazione) => this.formattaData(record?.dataPrimaAttivazione),
    },
    dataAttivazione: {
      title: 'Data attivazione',
      sortable: true,
      // quando nel periodo ci sono solo pacchetti aggiuntivi la riga mostra le date del
      // pacchetto utenza che li copre, che stanno fuori dal periodo selezionato
      value: (record: Attivazione) =>
        this.formattaData(record?.dataAttivazione) +
        (record && record.utenzaNelPeriodo === false ? ' (fuori periodo)' : ''),
    },
    dataScadenza: {
      title: 'Data scadenza',
      sortable: true,
      value: (record: Attivazione) => this.formattaData(record?.dataScadenza),
    },
    nrPacchettiAggiuntivi: {
      title: 'Aggiuntivi',
      sortable: false,
      align: 'center',
      value: (record: Attivazione) => String(record?.pacchettiAggiuntivi?.length || 0),
    },
    dettaglioAggiuntivi: {
      type: 'button',
      buttonIcon: 'visibility',
      title: 'Dettaglio pacchetti aggiuntivi',
      buttonId: 'dettaglio-aggiuntivi',
      buttonMostraSempre: true,
      sortable: false,
      nascondiButton: (record: Attivazione) => !record?.pacchettiAggiuntivi?.length,
    },
  };

  constructor(
    public dialogRef: MatDialogRef<DialogAttivazioniComponent>,
    public dialog: MatDialog,
    private readonly formBuilder: FormBuilder,
    private readonly licenzeService: LicenzeService,
    private readonly utenteService: UtenteService,
    private readonly utilityService: UtilityService,
  ) {}

  ngOnInit(): void {
    const annoCorrente = new Date().getFullYear();

    for (let anno = annoCorrente + 1; anno >= annoCorrente - 5; anno--) {
      this.anni.push(anno);
    }

    this.formParametri = this.formBuilder.group({
      modalitaPeriodo: ['anno', Validators.required],
      anno: [annoCorrente, Validators.required],
      dataDa: [null],
      dataA: [null],
      codicePromotore: [null],
      ruolo: [null],
      tipoRinnovo: [null],
    });

    this.formParametri.get('modalitaPeriodo')?.valueChanges.subscribe((modalita) => {
      this.aggiornaValidatoriPeriodo(modalita);
    });

    this.caricaDatiFiltri();
  }

  /**
   * L'anno e l'intervallo sono alternativi: valida solo i campi della modalità attiva
   */
  private aggiornaValidatoriPeriodo(modalita: 'anno' | 'intervallo') {
    const anno = this.formParametri.get('anno');
    const dataDa = this.formParametri.get('dataDa');
    const dataA = this.formParametri.get('dataA');

    if (modalita === 'intervallo') {
      anno?.clearValidators();
      dataDa?.setValidators(Validators.required);
      dataA?.setValidators(Validators.required);
    } else {
      anno?.setValidators(Validators.required);
      dataDa?.clearValidators();
      dataA?.clearValidators();
    }

    anno?.updateValueAndValidity({ emitEvent: false });
    dataDa?.updateValueAndValidity({ emitEvent: false });
    dataA?.updateValueAndValidity({ emitEvent: false });
  }

  private caricaDatiFiltri() {
    this.licenzeService.getPromotoriList().subscribe({
      next: (esito: PromotoreLicenze[]) => {
        this.promotori = esito || [];
      },
      error: (err: any) => {
        console.error(err);
        this.promotori = [];
      },
    });

    this.utenteService
      .getRuoli()
      .pipe(map((esito: RuoliList[]) => esito || []))
      .subscribe({
        next: (esito: RuoliList[]) => {
          this.ruoli = esito;
        },
        error: (err: any) => {
          console.error(err);
          this.ruoli = [];
        },
      });
  }

  public fnCaricamentoDati: FnCaricamentoDati = (
    page: number,
    pageSize: number,
    ricerca?: string,
    filters?: Filters[],
    sortBy?: SortBy[],
  ) => {
    if (!this.filtriEseguiti) {
      // Nessuna esecuzione richiesta: tabella vuota, nessuna chiamata al ws
      return of({ content: [], totalElements: 0 } as any);
    }

    this.sortByCorrente = sortBy?.length ? sortBy : this.sortByDefault();

    return this.licenzeService.getAttivazioni(
      page,
      pageSize,
      ricerca,
      this.filtriEseguiti,
      this.sortByCorrente,
    );
  };

  /**
   * Esegue la selezione con i parametri impostati e ricarica la tabella
   */
  public esegui() {
    this.formParametri.markAllAsTouched();

    if (this.formParametri.invalid) {
      this.utilityService.openSnackBar('Compila i parametri obbligatori', '', {
        duration: 3000,
        panelClass: ['red-snackbar'],
      });
      return;
    }

    const parametri = this.formParametri.getRawValue() as ParametriAttivazioni;

    if (parametri.modalitaPeriodo === 'intervallo' && parametri.dataDa && parametri.dataA) {
      if (parametri.dataDa > parametri.dataA) {
        this.utilityService.openSnackBar('La data di inizio deve precedere la data di fine', '', {
          duration: 3000,
          panelClass: ['red-snackbar'],
        });
        return;
      }
    }

    this.filtriEseguiti = this.licenzeService.costruisciFiltriAttivazioni(parametri);
    this.sortByCorrente = this.sortByDefault();

    if (this.tabella?.paginator && this.tabella.paginator.pageIndex !== 0) {
      // firstPage emette l'evento page, che ricarica già la tabella
      this.tabella.paginator.firstPage();
    } else {
      this.tabella?.caricaDati();
    }
  }

  /**
   * Esporta in excel le attivazioni con i parametri dell'ultima esecuzione
   */
  public esporta() {
    if (!this.filtriEseguiti) {
      this.utilityService.openSnackBar('Esegui prima la selezione', '', {
        duration: 3000,
        panelClass: ['red-snackbar'],
      });
      return;
    }

    this.spinnerOver.show();

    this.licenzeService
      .exportAttivazioni(this.tabella?.searchValue, this.filtriEseguiti, this.sortByCorrente)
      .pipe(finalize(() => this.spinnerOver.hide()))
      .subscribe({
        next: (esito: any) => {
          if (esito?.file) {
            this.utilityService.downloadExcel(esito.file, esito.fileName || this.nomeFileExport());
            this.utilityService.openSnackBar('Esportazione completata', '', {
              duration: 3000,
              panelClass: ['success-snackbar'],
            });
          } else {
            this.utilityService.openSnackBar('Nessun dato da esportare', '', {
              duration: 3000,
              panelClass: ['red-snackbar'],
            });
          }
        },
        error: (err: any) => {
          console.error(err);
          this.utilityService.openSnackBar('Esportazione non riuscita', '', {
            duration: 3000,
            panelClass: ['red-snackbar'],
          });
        },
      });
  }

  public bottoniListaClick(event: BottoniListaEvent) {
    switch (event.id) {
      case 'dettaglio-aggiuntivi':
        this.apriDettaglioAggiuntivi(event.data);
        break;
      default:
        console.warn('ATTENZIONE: bottone ' + event.id + ' non riconosciuto');
    }
  }

  private apriDettaglioAggiuntivi(attivazione: Attivazione) {
    this.dialog.open(DialogPacchettiAggiuntiviComponent, {
      data: {
        attivazione,
      },
      panelClass: ['dialog-container'],
      disableClose: false,
      width: '70%',
      maxHeight: '90%',
      autoFocus: false,
    });
  }

  private sortByDefault(): SortBy[] {
    return [
      { chiave: 'dataAttivazione', sort: 'asc' },
      { chiave: 'idUtente', sort: 'asc' },
    ];
  }

  private nomeFileExport(): string {
    const parametri = this.formParametri.getRawValue() as ParametriAttivazioni;

    const periodo =
      parametri.modalitaPeriodo === 'anno'
        ? String(parametri.anno)
        : [parametri.dataDa, parametri.dataA]
            .map((data) => (data ? new Date(data).toLocaleDateString('it-IT') : ''))
            .join('_');

    return 'Attivazioni_' + periodo + '.xlsx';
  }

  private descrizioneUtente(record: Attivazione): string {
    const generalita = [record?.cognome, record?.nome]
      .filter((val) => !!val)
      .join(' ')
      .trim();

    return generalita || record?.emailAttivazione || record?.keyId || '-';
  }

  private uniscoValori(valori?: string[]): string {
    return valori?.length ? valori.join(', ') : '-';
  }

  private rinnovoAutomatico(record: Attivazione): string {
    if (!record?.tipoRinnovo) {
      return '-';
    }

    return record.tipoRinnovo === 'AUTOMATICO' ? 'SI' : 'NO';
  }

  /**
   * Le date arrivano dal backend nel formato yyyy-MM-dd
   */
  private formattaData(data?: string): string {
    if (!data) {
      return '-';
    }

    const dataConvertita = new Date(data);

    return isNaN(dataConvertita.getTime()) ? data : dataConvertita.toLocaleDateString('it-IT');
  }
}
